import { Flex } from '@chakra-ui/react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { PropTypes } from 'prop-types';
import React from 'react';
import { useMountedState } from 'react-use';
import clientApi from '~/clientApi';
import Button from '~/components/Button';
import LinkButton from '~/components/LinkButton';
import { CONTENT_TYPES, SOCIAL_PLATFORMS } from '~/constants';
import { TIKTOK_CONTENT_QUERY_KEY } from '~/queries/keys';
import { selectContentModalTypes } from '~/types/content';
import { pluralize } from '~/utils';

import { Modal } from '../Modal';
import { SelectedContentPreview } from '../SelectedContentPreview';
import { SelectTiktokComponent } from './SelectTiktokModal.component';
import { flatten } from './SelectTiktokModal.helpers';

const propTypes = {
  ...selectContentModalTypes,
  titles: PropTypes.shape({
    modal: PropTypes.node,
    selectedPreview: PropTypes.node,
  }),
  creatorId: PropTypes.string,
};

const PAGE_SIZE = 20;

const fetchTiktokItems = async (query, creatorId) =>
  clientApi.ttContents.index({
    ...query,
    _impersonateOnce: creatorId,
  });

const fetchInitialContent = async (ids, creatorId) => {
  const promises = ids.map((id) =>
    clientApi.ttContents.read({ id, _impersonateOnce: creatorId }),
  );
  return await Promise.all(promises);
};

const defaultTitles = {
  modal: 'Choose your TikTok Highlights',
  selectedPreview: 'Selected highlights:',
};

export const SelectTiktokModal = ({
  cancelButtonText = 'Cancel',
  confirmButtonText = 'Save Changes',
  disabledItemIds = [],
  fetchContentFn = fetchTiktokItems,
  fetchInitialContentFn = fetchInitialContent,
  initialSelectedItemIds = [],
  isOpen,
  maxItems = 6,
  minItems = 3,
  onCancel,
  onClose,
  onSubmit,
  shouldShowSelectionPreview,
  titles: titlesProp = {},
  creatorId,
}) => {
  const titles = { ...defaultTitles, ...titlesProp };
  const [content, setContent] = React.useState([]);
  const [isSubmitDisabled, setIsSubmitDisabled] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [selectedItemIds, setSelectedItemIds] = React.useState(
    initialSelectedItemIds,
  );
  const [selectedContent, setSelectedContent] = React.useState([]);
  const isMounted = useMountedState();

  const queryParams = {
    pageSize: PAGE_SIZE,
  };

  const {
    hasNextPage,
    data = {
      pages: [],
    },
    fetchNextPage,
    isFetchingNextPage,
    status,
  } = useInfiniteQuery(
    [TIKTOK_CONTENT_QUERY_KEY, queryParams],
    ({ pageParam: page = 1 }) =>
      fetchContentFn({ ...queryParams, page }, creatorId),
    {
      getNextPageParam: (_group, groups) => {
        const { results, totalItems } = flatten(groups);

        return results.length < totalItems ? groups.length + 1 : false;
      },
      enabled: isOpen,
    },
  );

  const resetSelectedItems = () => setSelectedItemIds(initialSelectedItemIds);

  const handleClose = () => {
    resetSelectedItems();
    onClose();
  };

  const handleCancel = () => {
    if (onCancel) {
      resetSelectedItems();
      onCancel();
    } else {
      handleClose();
    }
  };

  const handleSubmit = async () => {
    const selectedItems = selectedItemIds.map(
      (contentId) => `/api/tiktok-contents/${contentId}`,
    );
    setIsSubmitting(true);

    await onSubmit(
      selectedItems,
      CONTENT_TYPES.tiktok,
      SOCIAL_PLATFORMS.tiktok,
    );

    if (isMounted()) {
      setSelectedItemIds(initialSelectedItemIds);
      setIsSubmitting(false);
    }
  };

  const handleItemClick = (id) => {
    const isSelected = selectedItemIds.includes(id);
    const hasReachedMax = maxItems <= selectedItemIds.length;

    if (!isSelected && hasReachedMax) return;

    if (isSelected) {
      setSelectedItemIds(selectedItemIds.filter((itemId) => id !== itemId));
    } else {
      setSelectedItemIds([...selectedItemIds, id]);
    }
  };

  React.useEffect(() => {
    if (data.pages.length) {
      const contentShallow = flatten(data.pages);
      setContent(contentShallow);
    }
  }, [data]);

  React.useEffect(() => {
    if (initialSelectedItemIds.length && content.length) {
      const selectedItemIdsShallow = initialSelectedItemIds.reduce(
        (acc, id) => {
          const Item = content.find((item) => id === item.id);
          if (Item) {
            acc.push(Item.id);
          }
          return acc;
        },
        [],
      );
      setSelectedItemIds(selectedItemIdsShallow);
    }
  }, [content, initialSelectedItemIds]);

  const initialStringified =
    !!initialSelectedItemIds.length && JSON.stringify(initialSelectedItemIds);
  const selectedStringified = JSON.stringify(selectedItemIds);

  // Fetch the contents of the items initially selected.
  // This is used to show the selected contents preview up top,
  // since they might not be fetched yet due to pagination
  React.useEffect(() => {
    if (initialStringified) {
      // eslint-disable-next-line no-inner-declarations
      async function fetchData() {
        const response = await fetchInitialContentFn(
          initialSelectedItemIds,
          creatorId,
        );

        setSelectedContent(response);
      }
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialStringified]);

  // Update the list of selected contents whenever the selected items change.
  React.useEffect(() => {
    if (content?.results) {
      setSelectedContent(
        selectedItemIds
          .map((id) => {
            // If the selected item is already visible on the items list, return that content
            // else return it from the already selected contents
            return (
              content.results.find((item) => item.id === id) ||
              selectedContent.find((selectedItem) => selectedItem.id === id)
            );
          })
          .filter((item) => !!item),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content?.results, selectedStringified]);

  const count = selectedItemIds.length;
  React.useEffect(() => {
    setIsSubmitDisabled(count < minItems || count > maxItems);
  }, [count, maxItems, minItems]);
  const previewError =
    count < minItems
      ? `You have to select at least ${minItems} ${pluralize(
          'video',
          'videos',
          minItems,
        )}`
      : undefined;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" isFullScreenOnMobile>
      <Modal.Header title={titles.modal} />
      {shouldShowSelectionPreview && (
        <SelectedContentPreview
          contentType={CONTENT_TYPES.tiktok}
          error={previewError}
          maxItems={maxItems}
          minItems={minItems}
          onChange={handleItemClick}
          selectedContent={selectedContent}
          title={titles.selectedPreview}
        />
      )}
      <Flex alignItems="center">
        {`Choose ${pluralize('one', 'up to %n', maxItems)}:`}
      </Flex>

      <Modal.Body
        marginTop={{ base: 4, md: 6 }}
        maxHeight={{ base: 'inherit', md: 500 }}
        overflowY="auto"
      >
        <SelectTiktokComponent
          content={content.results}
          disabledItemIds={disabledItemIds}
          hasMore={!!hasNextPage}
          isFetching={!!isFetchingNextPage}
          maxItems={maxItems}
          onItemClick={handleItemClick}
          onLoadMore={fetchNextPage}
          selectedItemIds={selectedItemIds}
          status={status}
        />
      </Modal.Body>

      <Modal.Footer>
        <LinkButton fontSize="md" colorScheme="primary" onClick={handleCancel}>
          {cancelButtonText}
        </LinkButton>

        <Button
          colorScheme="secondary"
          isDisabled={isSubmitDisabled}
          isLoading={isSubmitting}
          onClick={handleSubmit}
        >
          {confirmButtonText}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

SelectTiktokModal.propTypes = propTypes;
